import React, { useState, useEffect } from 'react';
import ToolLayout from './ToolLayout';

const FESTIVALS = [
  { id: 'sundance', name: 'Sundance Film Festival', tier: 'A-List', window: 'Jan', fee: 75, type: 'Narrative / Doc' },
  { id: 'sxsw', name: 'SXSW Film Festival', tier: 'A-List', window: 'Mar', fee: 70, type: 'Narrative / Doc' },
  { id: 'tribeca', name: 'Tribeca Film Festival', tier: 'A-List', window: 'Jun', fee: 70, type: 'Narrative / Doc' },
  { id: 'tiff', name: 'Toronto (TIFF)', tier: 'A-List', window: 'Sep', fee: 65, type: 'Narrative / Doc' },
  { id: 'afi', name: 'AFI Fest', tier: 'A-List', window: 'Oct', fee: 60, type: 'Narrative' },
  { id: 'nyff', name: 'New York Film Festival', tier: 'Prestige', window: 'Oct', fee: 0, type: 'Narrative (Invite)' },
  { id: 'cannes', name: 'Cannes Film Festival', tier: 'Prestige', window: 'May', fee: 0, type: 'Narrative (Invite)' },
  { id: 'venice', name: 'Venice Film Festival', tier: 'Prestige', window: 'Aug', fee: 0, type: 'Narrative (Invite)' },
  { id: 'slamdance', name: 'Slamdance Film Festival', tier: 'Indie', window: 'Jan', fee: 40, type: 'Narrative' },
  { id: 'fantastic', name: 'Fantastic Fest', tier: 'Genre', window: 'Sep', fee: 60, type: 'Genre' },
  { id: 'fantasia', name: 'Fantasia International', tier: 'Genre', window: 'Jul', fee: 45, type: 'Genre' },
  { id: 'orlando', name: 'Orlando Film Festival', tier: 'Regional', window: 'Oct', fee: 55, type: 'Narrative', note: '★ Aperture Slate Award Winner' },
  { id: 'sandiego', name: 'San Diego Film Festival', tier: 'Regional', window: 'Sep', fee: 50, type: 'Narrative', note: '★ Aperture Slate Award Winner' },
  { id: 'hotdocs', name: 'Hot Docs', tier: 'Documentary', window: 'Apr', fee: 45, type: 'Documentary' },
  { id: 'docnyc', name: 'DOC NYC', tier: 'Documentary', window: 'Nov', fee: 45, type: 'Documentary' },
  { id: 'fullframe', name: 'Full Frame Doc Festival', tier: 'Documentary', window: 'Apr', fee: 40, type: 'Documentary' },
];

const STATUSES = [
  { value: 'pending', label: 'Not Submitted', color: 'text-zinc-500', dot: 'bg-zinc-700' },
  { value: 'submitted', label: 'Submitted', color: 'text-sky-400', dot: 'bg-sky-500' },
  { value: 'accepted', label: 'Accepted ✓', color: 'text-emerald-400', dot: 'bg-emerald-500' },
  { value: 'rejected', label: 'Not Selected', color: 'text-red-400', dot: 'bg-red-600' },
  { value: 'withdrawn', label: 'Withdrawn', color: 'text-zinc-600', dot: 'bg-zinc-800' },
];

const TIERS = ['All', 'A-List', 'Prestige', 'Indie', 'Genre', 'Regional', 'Documentary'];

const TIER_COLORS = {
  'A-List': 'text-sky-400 border-sky-800',
  'Prestige': 'text-violet-400 border-violet-800',
  'Indie': 'text-emerald-400 border-emerald-800',
  'Genre': 'text-amber-400 border-amber-800',
  'Regional': 'text-orange-400 border-orange-800',
  'Documentary': 'text-rose-400 border-rose-800',
};

function loadState() {
  try {
    return JSON.parse(localStorage.getItem('as-festival-tracker') || '{}');
  } catch {
    return {};
  }
}

export default function FestivalTracker() {
  const [statuses, setStatuses] = useState(loadState);
  const [filterTier, setFilterTier] = useState('All');

  useEffect(() => {
    localStorage.setItem('as-festival-tracker', JSON.stringify(statuses));
  }, [statuses]);

  const setStatus = (id, value) => {
    setStatuses((prev) => ({ ...prev, [id]: value }));
  };

  const getStatus = (id) => statuses[id] || 'pending';

  const submitted = FESTIVALS.filter((f) => getStatus(f.id) !== 'pending').length;
  const accepted = FESTIVALS.filter((f) => getStatus(f.id) === 'accepted').length;
  const totalFees = FESTIVALS.filter((f) => ['submitted', 'accepted', 'rejected'].includes(getStatus(f.id)))
    .reduce((s, f) => s + f.fee, 0);

  const filtered = filterTier === 'All' ? FESTIVALS : FESTIVALS.filter((f) => f.tier === filterTier);

  const reset = () => {
    if (window.confirm('Reset all festival statuses? This cannot be undone.')) {
      setStatuses({});
    }
  };

  return (
    <ToolLayout
      accent="Aperture Slate // Distribution Tools"
      title="Festival"
      titleAccent="Tracker"
      description="Track your film's submission status across 16 major festivals. Your progress is saved to your browser."
    >
      <div className="space-y-5">

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3">
          <div className="border border-zinc-800 bg-zinc-950 p-4 text-center">
            <p className="text-[10px] tracking-widest text-zinc-500 uppercase mb-2">Submitted</p>
            <p className="text-2xl font-black text-white">{submitted}</p>
            <p className="text-[10px] text-zinc-600">of {FESTIVALS.length}</p>
          </div>
          <div className="border border-zinc-800 bg-zinc-950 p-4 text-center">
            <p className="text-[10px] tracking-widest text-zinc-500 uppercase mb-2">Accepted</p>
            <p className="text-2xl font-black text-emerald-400">{accepted}</p>
            <p className="text-[10px] text-zinc-600">selections</p>
          </div>
          <div className="border border-zinc-800 bg-zinc-950 p-4 text-center">
            <p className="text-[10px] tracking-widest text-zinc-500 uppercase mb-2">Fees Paid</p>
            <p className="text-2xl font-black text-sky-400">${totalFees}</p>
            <p className="text-[10px] text-zinc-600">estimated</p>
          </div>
        </div>

        {/* Filter */}
        <div className="flex flex-wrap gap-2">
          {TIERS.map((t) => (
            <button
              key={t}
              onClick={() => setFilterTier(t)}
              className={`text-[10px] font-bold tracking-widest px-3 py-1.5 border transition-all ${
                filterTier === t
                  ? 'border-sky-500 text-sky-400 bg-sky-500/10'
                  : 'border-zinc-800 text-zinc-600 hover:border-zinc-600 hover:text-zinc-400'
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Festival List */}
        <div className="border border-zinc-800 bg-zinc-950 divide-y divide-zinc-900">
          {filtered.map((festival) => {
            const status = getStatus(festival.id);
            const statusDef = STATUSES.find((s) => s.value === status);
            return (
              <div key={festival.id} className="p-4 flex items-center gap-4">
                {/* Status dot */}
                <div className={`w-2 h-2 rounded-full shrink-0 ${statusDef.dot}`} />

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-bold text-white tracking-wide">{festival.name}</span>
                    {festival.note && (
                      <span className="text-[10px] text-sky-400 font-bold">{festival.note}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-0.5">
                    <span className={`text-[10px] font-bold tracking-widest border px-1.5 py-0.5 ${TIER_COLORS[festival.tier]}`}>
                      {festival.tier}
                    </span>
                    <span className="text-[10px] text-zinc-600">{festival.window}</span>
                    <span className="text-[10px] text-zinc-600">{festival.type}</span>
                    {festival.fee > 0 && (
                      <span className="text-[10px] text-zinc-600">~${festival.fee} fee</span>
                    )}
                    {festival.fee === 0 && (
                      <span className="text-[10px] text-zinc-700">Invite only</span>
                    )}
                  </div>
                </div>

                {/* Status select */}
                <select
                  value={status}
                  onChange={(e) => setStatus(festival.id, e.target.value)}
                  className="bg-zinc-900 border border-zinc-700 text-[11px] text-zinc-300 px-2 py-1.5 tracking-wide shrink-0 focus:outline-none focus:border-sky-500"
                >
                  {STATUSES.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
            );
          })}
        </div>

        <div className="flex justify-between items-center">
          <p className="text-[10px] text-zinc-700 leading-relaxed">
            Data saved to your browser. Not synced across devices.
          </p>
          <button
            onClick={reset}
            className="text-[10px] text-zinc-700 hover:text-red-500 tracking-widest uppercase transition-colors"
          >
            Reset All
          </button>
        </div>
      </div>
    </ToolLayout>
  );
}
